import { createContext, type ReactNode, useContext } from 'react'

interface PrintHeaderProps {
  kitchenName: string
  dateLabel: string
}

/**
 * A notice every PrintHeader beneath the provider prints beside its date, so a
 * packet can mark each page without threading a prop through every print
 * component (the chef packet's "INCOMPLETE ORDER" label, PROD-1452).
 */
export const PrintHeaderNoticeContext = createContext<ReactNode>(null)

/** The Ordo wordmark as inline SVG: it survives the PDF route's markup allowlist, an `<img>` would not. */
export function OrdoWordmark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 898.1 496.8"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      focusable="false">
      <g fill="currentColor">
        <path d="M64.2,302.9c0-48,39.9-86.7,89.4-86.7c49.2,0,89.4,36.6,89.4,86.7c0,48.3-40.2,87.3-89.4,87.3 C104.1,390.2,64.2,353.3,64.2,302.9z M183.7,356.9c18.3-6,17.7-32.4,6.9-59.4c-11.1-27.6-40.8-60.3-65.1-52.2 c-17.4,5.7-18.6,33.6-5.4,63.9C132.6,338.3,160,364.7,183.7,356.9z" />
        <path d="M259.6,374c0-14.1,15.3-12.6,26.1-27c7.5-9.9,9.3-21,9.3-40.2c0-36-20.4-33.6-28.8-47.7 c-3.9-6.6-0.9-13.2,5.4-18c7.2-5.4,14.1-5.4,27.6-13.8c15.6-9.9,23.4-17.4,34.2-13.8c10.8,3.3,10.8,18.9,8.1,36.6 c-0.6,3.3-0.9,6.9-1.2,10.5c16.8-27.3,48.6-51.3,65.7-53.7c9.3-1.2,15.9-0.6,15.3,8.7c-0.3,5.4-5.4,27.6-4.5,45.9 c0.9,17.1,6.6,33.9-4.5,38.4c-12,4.8-19.2-16.8-30-27.9c-11.1-11.4-41.4-10.2-43.5,37.5c0.6,19.8,2.7,28.8,9.3,37.5 c11.1,14.4,34.5,8.4,34.2,25.8c-0.3,9.9-8.4,15.3-17.4,15.3s-29.1-2.4-42-2.4c-12.6,0-29.7,2.4-45,2.4 C266.8,388.1,259.6,381.8,259.6,374z" />
        <path d="M436.4,302.9c0-48,35.7-87,81-87c24,0,38.1,10.8,46.8,23.1v-14.7c0-53.4-27-30.6-32.1-46.2 c-5.1-15.9,7.2-13.8,39-25.5c19.5-7.2,33-23.1,44.1-12.9c10.5,9.6-7.2,31.2-7.2,108.4v37.2c0,25.2,4.2,44.7,12.9,58.2 c12.6,19.8,22.2,24.9,10.2,36.9c-11.7,11.7-22.5,7.5-53.7-13.8c-12.9,14.7-30.9,23.7-54,23.7C475.1,390.2,436.4,353.3,436.4,302.9z M538.1,359.3c20.1,0.3,29.7-19.2,28.5-48.3c-1.2-29.7-16.2-65.1-46.2-66c-22.5-0.9-29.7,20.1-27.9,53.1 C494.6,329.6,512,359,538.1,359.3z" />
        <path d="M643.8,302.9c0-48,39.9-86.7,89.4-86.7c49.2,0,89.4,36.6,89.4,86.7c0,48.3-40.2,87.3-89.4,87.3 C683.7,390.2,643.8,353.3,643.8,302.9z M763.2,356.9c18.3-6,17.7-32.4,6.9-59.4c-11.1-27.6-40.8-60.3-65.1-52.2 c-17.4,5.7-18.6,33.6-5.4,63.9C712.2,338.3,739.5,364.7,763.2,356.9z" />
        <path d="M146.5,184.1c-3,0-6.3-0.2-10-1c-8-1.7-10.6-5.4-9.5-13.5c1.1-8.3,2.9-16.5,4.2-24.8c2-13,0.9-25.9-2.2-38.7 c-1.4-5.7-2.2-11.5-2.7-17.3c-0.5-5.2,1.6-8.3,6.6-9.9c10.5-3.3,20.7-3.4,31.3-0.7c5.7,1.5,7.7,4.8,7.3,10.7 c-0.2,3.2-0.4,6.5-1.5,9.5c-5.5,16.2-4,32.5-1.1,48.9c1.3,7.5,2,15.2,2.6,22.9c0.5,6.3-1.5,9.2-7.3,11.7c-2,0.8-4.3,1.5-6.4,1.7 C154.4,184.1,150.9,184,146.5,184.1z" />
      </g>
    </svg>
  )
}

/**
 * The context's notice on its own, for a page heading that is not a
 * PrintHeader: a chef packet sheet repeats a table `<thead>` on every printed
 * page, so the "INCOMPLETE ORDER" label rides in that header instead.
 */
export function PrintHeaderNotice({ className }: { className?: string }) {
  const notice = useContext(PrintHeaderNoticeContext)
  return notice ? <span className={className}>{notice}</span> : null
}

export function PrintHeader({ kitchenName, dateLabel }: PrintHeaderProps) {
  const notice = useContext(PrintHeaderNoticeContext)
  return (
    <div className="mb-4 flex items-center justify-between border-b border-black/15 pb-4 text-black">
      <div className="flex items-center gap-[9px]">
        <OrdoWordmark className="h-4 w-auto" />
        <span className="h-4 w-px self-stretch bg-black/15" aria-hidden />
        <span className="text-[10px] font-medium leading-[1.5] tracking-[0.15px]">{kitchenName}</span>
      </div>
      <div className="flex items-center gap-2">
        {notice}
        <span className="text-[10px] font-medium leading-[1.6] tracking-[0.15px]">{dateLabel}</span>
      </div>
    </div>
  )
}
